import type { HotelAvgRoomNight } from '@travel-planner/shared/schemas';
import type { CardFacts } from '@travel-planner/shared/schemas/card-facts';
import type {
  PlannerServiceSpan,
  PlannerTransportVehicle,
} from '@travel-planner/shared/utils/planner-day-services';
import type {
  PlannerTransferAssistances,
  PlannerTransferKind,
  PlannerTransferPlaces,
  PlannerTransferTimes,
  PlannerTransferVehicles,
} from '@travel-planner/shared/utils/planner-transfers';
export const advisorProductTypes = [
  'hotels',
  'experiences',
  'restaurants',
] as const;

export type AdvisorProductType = (typeof advisorProductTypes)[number];

export const advisorItemKinds = ['hotel', 'experience', 'restaurant'] as const;

export type AdvisorItemKind = (typeof advisorItemKinds)[number];

export type AdvisorRegion = {
  id: string;
  title: string;
  description: string | null;
  countries: string[];
  parentId: string | null;
  labels: string[];
  /** The region's outline on the map; empty when none is drawn. */
  boundary?: AdvisorPoint[];
  /** Cities the region lists in XT (for products with no map point). */
  cities?: string[];
};

export type AdvisorPoint = { lat: number; lng: number };

export type AdvisorProduct = {
  id: string;
  title: string;
  subtitle: string | null;
  tags: string[];
  clientPrice: number | null;
  /** A per-person product's Child rate; null: children pay `clientPrice`. */
  childPrice?: number | null;
  currency: string | null;
  kind: AdvisorItemKind;
  regionId: string | null;
  regionTitle: string;
  thumbnail: string | null;
  /** Map point; drives "To hotel" distances. */
  location?: AdvisorPoint | null;
  /** From the product's first leg start → end; null when it has none. */
  durationMinutes?: number | null;
  /** What the price is per (`Pax`, `Night`, `Piece`, …); null when unset. */
  pricingType?: string | null;
  country?: string | null;
  /**
   * Hotels only: average historical client SELL PRICE per room per night from
   * sold itineraries. Rendered when the host sets `showAvgRoomNight`.
   */
  avgRoomNight?: HotelAvgRoomNight | null;
  /** First sentence of the English short marketing text (XT-840). */
  strapline?: string | null;
  /** The first leg's start and end, minutes past midnight (XT-840). */
  startMinutes?: number | null;
  endMinutes?: number | null;
  /** Rooms, format, languages, pace, capacity, closed days, private room. */
  facts?: CardFacts | null;
  /** The product's Labels, read as booking conditions ("Book 48h ahead"). */
  conditions?: readonly string[];
  /** Hotels: star class (half stars kept); null when unrated. */
  stars?: number | null;
  /** Catalogue subcategory — day-suggest diversity key. */
  subcategory?: string | null;
  /**
   * Sold-stage trip count in the org/country slice (day-suggest rank).
   * Set on internal XT rows from `suggestCatalog`; omitted when stripped
   * for the agent portal (`stripEvidence`).
   */
  soldTrips?: number;
  /** Cancelled-stage placements — demotes day-suggest rank. */
  cancelledTrips?: number;
  /**
   * True when the row came from `suggestCatalog` picks — already ranked
   * server-side; client must not re-score.
   */
  preRanked?: boolean;
};

/** Travellers booked on one item (XT-840). */
export type AdvisorGuests = { adults: number; children: number };

export type AdvisorDraftItemOrigin = 'manual' | 'suggested';

/** T-357: whether the DMC quotes a base hotel or the agent already booked it. */
export const advisorHotelBookings = ['quote', 'booked'] as const;

export type AdvisorHotelBooking = (typeof advisorHotelBookings)[number];

export type AdvisorDraftItem = {
  id: string;
  productId: string;
  title: string;
  kind: AdvisorItemKind;
  clientPrice: number | null;
  /**
   * A per-person product's Child rate, copied when added; null or absent
   * (older drafts): children pay `clientPrice`, the Adult rate.
   */
  childPrice?: number | null;
  currency: string | null;
  subtitle: string | null;
  thumbnail: string | null;
  origin: AdvisorDraftItemOrigin;
  /**
   * HH:MM wall clock on the stay day: the product's usual time, the day's
   * next free time or the agent's. Null only on hotels and older drafts.
   */
  startTime: string | null;
  /** HH:MM the agent set the item to end; absent: start plus its length. */
  endTime?: string | null;
  /** Builder slot when generated (`hotel` / `lunch` / `transfer` / …). */
  slot?: string | null;
  /** Copied from the product when added; older drafts have none. */
  location?: AdvisorPoint | null;
  pricingType?: string | null;
  durationMinutes?: number | null;
  /**
   * A hotel's historical average per room per night (XT-817), copied from the
   * product so the estimate can stand in for a price still on request.
   */
  avgRoomNight?: HotelAvgRoomNight | null;
  /**
   * Generate items arrive without price, map point or duration: `true` until
   * the catalogue row has been looked up, then `false`.
   */
  detailsPending?: boolean;
  /** The agent removed the Guide this experience adds (XT-828). */
  guideRemoved?: boolean;
  /**
   * The agent's comment for the DMC on this item (XT-834) — the first
   * message about it in the day's comments once the request is sent.
   */
  comment?: string | null;
  /**
   * Booked for fewer than the whole party (XT-840): a per-person price counts
   * these travellers. Null or absent: everyone.
   */
  guests?: AdvisorGuests | null;
  /** A base hotel's Send me a quote / Already booked (T-357); null: unset. */
  hotelBooking?: AdvisorHotelBooking | null;
};

export type AdvisorDraftDay = {
  id: string;
  items: AdvisorDraftItem[];
  /** The agent removed the Transport the day's experiences add (XT-828). */
  transportRemoved?: boolean;
  /**
   * The cars the agent chose for the day's Transport, one line each; absent:
   * the vehicle that seats the day's largest group.
   */
  transportVehicles?: PlannerTransportVehicle[] | null;
  /**
   * The agent's half or full day for the day's Transport (T-326); on a day
   * whose experiences need no car, the Transport the agent added.
   */
  transportSpan?: PlannerServiceSpan | null;
  /** The trip transfers the agent removed from this day (XT-920). */
  transfersRemoved?: PlannerTransferKind[];
  /** The cars, or a flight or train, the agent chose for this day's transfers. */
  transferVehicles?: PlannerTransferVehicles | null;
  /** Where the trip's arrival starts / departure ends; absent: the airport. */
  transferPlaces?: PlannerTransferPlaces | null;
  /** The VIP airport assistance added to the arrival / departure (T-423). */
  transferAssistance?: PlannerTransferAssistances | null;
  /** The time the agent set on this day's transfers (T-425). */
  transferTimes?: PlannerTransferTimes | null;
  /**
   * The trip's last day, after its last night (Mini Planner v3): it holds the
   * departure's plans but no hotel night.
   */
  departure?: boolean;
  /**
   * Products the agent skipped or dismissed in this day's Suggestions: never
   * offered on this day again, and on other days only when nothing else fits.
   */
  dismissedProductIds?: string[];
};

/** A chapter of the trip: one region of one country, and its days. */
export type AdvisorDraftLeg = {
  id: string;
  regionId: string;
  regionTitle: string;
  country?: string | null;
  /** Where the chapter sleeps — one stay across all its nights. */
  baseHotel?: AdvisorDraftItem | null;
  days: AdvisorDraftDay[];
};

export type AdvisorDraft = {
  title: string;
  reference: string;
  legs: AdvisorDraftLeg[];
};
